import type { DjangoPaginatedResults } from '../../api-django'
import { createHttpEndpoint } from '../../utils'

import type {
  GetListingHistory,
  GetListingHistoryParams,
  GetListingOnlineConditions,
  GetListingOnlineConditionsParams,
  GetListingsAdminInformation,
  GetListingsAdminInformationParams,
} from './listings.types'

/**
 * Returns a list of listings.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings/get}
 */
export const getListings = createHttpEndpoint({
  method: 'GET',
  operationId: 'getListings',
  path: '/bm/catalog/listings',
  transformResponseToCamelCase: true,
})

/**
 * OLD SCHOOL EXPORT
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1export/post}
 */
export const postListingsExport = createHttpEndpoint({
  method: 'POST',
  operationId: 'csvExportRequest',
  path: '/bm/catalog/listings/export',
})

/**
 * Update a batch of listings asynchronously
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1batch-update/post}
 */
export const postListingsBatchUpdate = createHttpEndpoint({
  method: 'POST',
  operationId: 'csvUpdateRequest',
  path: '/bm/catalog/listings/batch-update',
})

/**
 * Import vanishing deals in bulk.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1vanishing-deal~1import/post}
 */
export const postVanishingDealsImport = createHttpEndpoint({
  method: 'POST',
  operationId: 'importVanishingDeals',
  path: '/bm/catalog/vanishing-deal/import',
  // TODO [PAYIN-2621] Add support for FormData
  // isFormData: true,
})

/**
 * Partially updates a specific listing
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1{id}/patch}
 */
export const patchListing = createHttpEndpoint({
  method: 'PATCH',
  operationId: 'updateListing',
  path: '/bm/catalog/listings/:listingId',
  transformResponseToCamelCase: true,
})

/**
 * Archive a listing
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1%7Bid%7D~1archive/post }
 */
export const postListingArchive = createHttpEndpoint({
  method: 'POST',
  operationId: 'archiveRequest',
  path: '/bm/catalog/listings/:listingId/archive',
})

/**
 * Return the online conditions of a listing in all markets or in some specific
 * markets
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1%7Bid%7D~1online-conditions/get}
 */
export const getListingOnlineConditions = createHttpEndpoint<
  GetListingOnlineConditions,
  GetListingOnlineConditionsParams
>({
  method: 'GET',
  operationId: 'getOnlineConditions',
  path: '/bm/catalog/listings/:listingId/online-conditions',
})

/**
 * Publish a listing
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1%7Bid%7D~1publish/post}
 */
export const postListingPublish = createHttpEndpoint({
  method: 'POST',
  operationId: 'publishRequest',
  path: '/bm/catalog/listings/:listingId/publish',
})

/**
 * Returns specific Listing with info available to admins only.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1%7Bid%7D~1admin/get}
 */
export const getListingAdminInformation =
  createHttpEndpoint<GetListingsAdminInformation>({
    method: 'GET',
    operationId: 'fetchListingInformation',
    path: '/bm/catalog/listings/:listingId/admin',
  })

/**
 * Returns a paginated list of Listings - available to admins only.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1admin/get}
 */
export const getListingsAdminInformation = createHttpEndpoint<
  DjangoPaginatedResults<GetListingsAdminInformation>,
  GetListingsAdminInformationParams
>({
  method: 'GET',
  operationId: 'getAdminListings',
  path: '/bm/catalog/listings/admin',
})

/**
 * Returns the history of a listing.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/paths/~1bm~1catalog~1listings~1%7Bid%7D~1history/get}
 */
export const getListingHistory = createHttpEndpoint<
  DjangoPaginatedResults<GetListingHistory>,
  GetListingHistoryParams
>({
  method: 'GET',
  operationId: 'fetchListingHistory',
  path: '/bm/catalog/listings/:listingId/history',
})

/**
 * Rematch a given listing to a target product.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/catalog-access-api/definition#tag/Listings/operation/post-bm-catalog-listings-id-rematch}
 */
export const postListingRematch = createHttpEndpoint({
  method: 'POST',
  operationId: 'rematchListing',
  path: '/bm/catalog/listings/:listingId/rematch',
})

/**
 * TODO Add link to API specs when available
 */
export const postListingsBatchUpsertWithProducts = createHttpEndpoint({
  method: 'POST',
  operationId: 'csvImportRequest',
  path: '/bm/catalog/listings/batch-upsert-with-products',
})
